import axios, { AxiosRequestConfig } from "axios";
import { logErrorToInsights } from "../ApplicationInsights";
import {
  checkAndLogErr,
  getTransId,
  compData,
  logNodeName,
  getDateForlogging
} from "../utils/util";
import { TokenManager } from "@forgerock/javascript-sdk";
import { resetSessionTimer } from "../redux/slices/userSlice";
import { store } from "../redux/store/index";



const getKeyInsensitive = (obj: any, key: string) => {
  if (!obj) return undefined;
  const foundKey = Object.keys(obj).find(
    (k) => k.toLowerCase() === key.toLowerCase()
  );
  return foundKey ? obj[foundKey] : undefined;
};

const getValueFromPath = (obj: any, path: string) => {
  return path.split(".").reduce((acc, part) => {
    if (!acc) return undefined;

    // 🔒 SAFE parsing instead of regex
    const open = part.indexOf("[");
    const close = part.indexOf("]");

    if (open > 0 && close === part.length - 1) {
      const key = part.slice(0, open);
      const indexStr = part.slice(open + 1, close);
      const arr = getKeyInsensitive(acc, key);
      return arr?.[indexStr];
    }

    return getKeyInsensitive(acc, part);
  }, obj);
};


const extractFailedValues = (
  errors: Record<string, string[]>,
  payload: any
) => {
  const result: Record<string, any> = {};
  if (!errors || !payload) return result;

  Object.keys(errors).forEach((errorPath) => {

    

    const value = getValueFromPath(payload, errorPath);
    result[errorPath] = {
      value,
      messages: errors[errorPath],
    };
  });

  return result;
};


const getFailedValuesString = (failedValues: Record<string, any>) => {
  return Object.entries(failedValues)
    .map(([key, value]) => JSON.stringify({ [key]: value }))
    .join(" | ");
};

// Set up request interceptor
axios.interceptors.request.use(
  async (config) => {
    const token = await TokenManager.getTokens();
    if (token) {
      config.headers.Authorization = `Bearer ${token.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Set up response interceptor
axios.interceptors.response.use(
  (response) => {
    store.dispatch(resetSessionTimer());
    return response;
  },
  async (error) => {
    const { config } = error;
    const originalRequest = config;

    // Check if the error status indicates that the token has expired
    if (error?.response?.status === 401) {
      // Update the token
      const token = await TokenManager.getTokens();

      // Re-assign new token to original request and retry the request
      if (token) {
        originalRequest.headers.Authorization = `Bearer ${token.accessToken}`;
        return axios(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

const apiCall = async (params: AxiosRequestConfig, data?: compData) => {
  try {
    const result = await axios(params);

    checkAndLogErr(result, params, data);

    return result.data;
  } catch (error: any) {
    const currentDate = getDateForlogging(new Date());
    let requestPayload = params?.data ?? data;
    if (typeof requestPayload === "string") {
      try {
        requestPayload = JSON.parse(requestPayload);
      } catch {
        // ignore
      }
    }

    const errorsObj =
      error?.response?.data?.errors?.errorObj ??
      error?.response?.data?.errors;

    const failedValues = extractFailedValues(errorsObj, requestPayload);

    const failedValuesString = getFailedValuesString(failedValues);

    const statusCode = error?.response?.status ?? "NA";

    logErrorToInsights(
      `${getTransId(params)},${currentDate},${logNodeName},${
          params.headers?.fileName || ""
        },${data?.company},${data?.product},${params?.url},${
        params?.method
      },${statusCode}, ${failedValuesString}`
    );

    return error
  }
};

export default apiCall;
